<template>
  <div
    :class="{
      busy: isBusy || isWaitingForValues || loading,
      'document-metada-select': true,
    }"
  >
    <PVSelect
      v-model="selectedOption"
      :options="possibleValues ?? []"
      :filter="true"
      :disabled="disabled || isWaitingForValues || loading"
      :class="{ 'p-invalid': isError || !isInputValidated }"
      fluid
      :auto-filter-focus="true"
      @change="handleSelectionChanged($event.value)"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useToast } from '@/composables/useToast';
  import PVSelect from 'primevue/select';
  import {
    ReloadMetadataFunction,
    UpdateMetadataFunction,
    ValidationFunction,
  } from '../DocumentsMetadata/DocumentsMetadata.types';

  interface DocumentMetadataSelectProps {
    disabled?: boolean;
    getMetadataFunction?: ReloadMetadataFunction;
    isError?: boolean;
    isWaitingForValues?: boolean;
    currentValue: string;
    metadataField: string;
    possibleValues?: string[];
    loading?: boolean;
    validationFunction: ValidationFunction;
    updateMetadataFunction: UpdateMetadataFunction;
  }

  const props = withDefaults(defineProps<DocumentMetadataSelectProps>(), {
    disabled: false,
    isError: false,
    isWaitingForValues: false,
  });

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const { createErrorToast } = useToast();

  const isBusy = ref(false);
  const isInputValidated = ref(true);
  const selectedOption = computed(() => {
    if (!props.possibleValues?.length) return props.currentValue;
    const exact = props.possibleValues.find((v) => v === props.currentValue);
    if (exact) return exact;
    if (props.metadataField === 'DocumentClass') {
      return (
        props.possibleValues.find((v) =>
          v.startsWith(`${props.currentValue} (`),
        ) ?? props.currentValue
      );
    }
    return props.currentValue;
  });

  function validateInput(): void {
    isInputValidated.value = props.validationFunction(selectedOption.value);
  }

  async function handleSelectionChanged(selection: string): Promise<void> {
    try {
      isBusy.value = true;
      await props.updateMetadataFunction(props.metadataField, selection);
      await props.getMetadataFunction?.();
    } catch {
      createErrorToast();
    } finally {
      validateInput();
      emit('document-data-changed');
      isBusy.value = false;
    }
  }

  watch(
    () => [selectedOption.value, props.validationFunction],
    () => {
      validateInput();
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .document-metada-select {
    &.busy {
      :deep(.p-select) {
        @include pulsating-animation;
      }
    }

    :deep(.p-invalid .p-select-label) {
      border-color: var(--p-inputtext-invalid-border-color);
    }

    :deep(.p-select.p-invalid) {
      border-color: var(--p-inputtext-invalid-border-color);
    }
  }
</style>
